import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { Box, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import {
    DeviceDetailsAccordion,
    DeviceDetailsBox,
    DeviceDetailsHeaderBox,
    DeviceDetailsItemRow,
    DeviceDetailsSectionBox,
    DeviceDetailsSectionTable,
    DeviceDetailsSectionTableSkeleton,
    DeviceStatusChip,
} from "./DeviceDetailsDialogElements"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { fetchDeviceDiagnostics } from "../deviceApi"
import { DeviceDetailsStatusDetails } from "../deviceTypes"

const useStyles = makeStyles()((theme) => ({
    sectionBox: {
        display: "grid",
        gap: "2rem",
    },
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "14px 1fr",
        gap: "10px",
    },
    infoIcon: {
        marginTop: "0.25rem",
        color: theme.palette.tailwind.sky[500],
    },
}))

type DeviceDetailsDialogStatusSectionProps = Readonly<{
    deviceId: number
    statusDetails: DeviceDetailsStatusDetails
}>

export function DeviceDetailsDialogStatusSection({ deviceId, statusDetails }: DeviceDetailsDialogStatusSectionProps) {
    const { classes } = useStyles()
    const { status, message, hasDiagnostics } = statusDetails
    const [expanded, setExpanded] = useState<boolean>(false)

    const deviceDetailsDiagnostics = useApiQuery(fetchDeviceDiagnostics, {
        queryName: "diagnostics-for-device",
        dependencies: {
            deviceId,
        },
        enabled: expanded,
    })

    return (
        <DeviceDetailsBox>
            <LocalizedStrict id="device-details-status-info-title" attrs={{ title: true }}>
                <DeviceDetailsHeaderBox title="Status">
                    <DeviceStatusChip status={status} />
                </DeviceDetailsHeaderBox>
            </LocalizedStrict>
            <DeviceDetailsSectionBox updateClasses={classes.sectionBox}>
                <Box className={classes.infoWrap}>
                    <FontAwesomeIcon icon={faInfoCircle} className={classes.infoIcon} />
                    <Typography variant="subtitle1">{message}</Typography>
                </Box>
                {hasDiagnostics && (
                    <LocalizedStrict id="device-details-status-info-diagnostics-title" attrs={{ title: true }}>
                        <DeviceDetailsAccordion title="Show diagnostics" expanded={expanded} setExpanded={setExpanded}>
                            {!deviceDetailsDiagnostics.isLoading ? (
                                <DeviceDetailsSectionTable
                                    headElements={
                                        <>
                                            <LocalizedStrict id="device-details-status-info-diagnostics-subject">
                                                <span>Subject</span>
                                            </LocalizedStrict>
                                            <LocalizedStrict id="device-details-status-info-diagnostics-message">
                                                <span>Message</span>
                                            </LocalizedStrict>
                                        </>
                                    }
                                >
                                    {deviceDetailsDiagnostics.data?.diagnostics.map((el, index) => (
                                        <DeviceDetailsItemRow key={index}>
                                            <Typography>{el.subject}</Typography>
                                            <Typography>{el.message}</Typography>
                                        </DeviceDetailsItemRow>
                                    ))}
                                </DeviceDetailsSectionTable>
                            ) : (
                                <DeviceDetailsSectionTableSkeleton />
                            )}
                        </DeviceDetailsAccordion>
                    </LocalizedStrict>
                )}
            </DeviceDetailsSectionBox>
        </DeviceDetailsBox>
    )
}
